import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  ClipboardList,
  Eye,
  Filter,
  Image as ImageIcon,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  X,
  XCircle,
} from 'lucide-react';
import { adminAPI, unitAPI } from '../services/api';
import {
  formatLocalizedDateTime,
  getInspectionFreeText,
  getInspectionItemText
} from '../utils/i18nFormatters';
import {
  getManilaCurrentMonthRange,
  isInspectionInManilaDateRange,
} from '../utils/inspectionMonthView';
import {
  getInspectionDisplayItemStatus,
  getInspectionDisplayOverallStatus,
  getInspectionDisplaySummary,
} from '../utils/operations';
import InspectionPhoto from './InspectionPhoto';
import { AdminModalSurface } from './AdminModal';

const reviewStatusMap = {
  pending_review: {
    labelKey: 'stewardCheckinRecords.reviewStatus.pending_review',
    className: 'border border-amber-300 bg-amber-50 text-amber-800',
  },
  approved: {
    labelKey: 'stewardCheckinRecords.reviewStatus.approved',
    className: 'border border-emerald-300 bg-emerald-50 text-emerald-800',
  },
  rejected: {
    labelKey: 'stewardCheckinRecords.reviewStatus.rejected',
    className: 'border border-rose-300 bg-rose-50 text-rose-800',
  },
};

const overallStatusMap = {
  normal: {
    labelKey: 'stewardCheckinRecords.overallStatus.normal',
    className: 'border border-sky-300 bg-sky-50 text-sky-800',
  },
};

const itemStatusMap = {
  completed: {
    labelKey: 'stewardCheckinRecords.itemStatus.completed',
    className: 'border border-emerald-300 bg-emerald-50 text-emerald-800',
  },
  pending: {
    labelKey: 'stewardCheckinRecords.itemStatus.pending',
    className: 'border border-slate-300 bg-slate-100 text-slate-700',
  },
};

const DEPRECATED_PRESSURE_ITEM_IDS = new Set(['4.2', '4.3']);

function getInspectionItemId(item) {
  return String(item?.itemId || item?.id || '').trim();
}

function isDeprecatedPressureItem(item) {
  return DEPRECATED_PRESSURE_ITEM_IDS.has(getInspectionItemId(item));
}

function getVisibleInspectionItems(inspection) {
  return (inspection?.items || []).filter((item) => !isDeprecatedPressureItem(item));
}

function buildBadge(t, meta, fallback = '-') {
  const resolvedMeta = meta || {
    labelKey: '',
    className: 'border border-slate-300 bg-slate-100 text-slate-700',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ${resolvedMeta.className}`}>
      {resolvedMeta.labelKey ? t(resolvedMeta.labelKey, { defaultValue: fallback }) : fallback}
    </span>
  );
}

function flattenPhotoUrls(inspection, options = {}) {
  const { includeDeprecated = false } = options;
  const urls = new Set();
  const items = includeDeprecated ? (inspection?.items || []) : getVisibleInspectionItems(inspection);

  items.forEach((item) => {
    (item.photoUrls || []).forEach((url) => {
      if (typeof url === 'string' && url.trim()) {
        urls.add(url);
      }
    });
  });

  return Array.from(urls);
}

function getInspectionPhotoCount(inspection, options = {}) {
  const items = inspection?.items || [];

  if (items.length > 0) {
    return flattenPhotoUrls(inspection, options).length;
  }

  const fallback = options.includeDeprecated
    ? inspection?.photoCount
    : (inspection?.visiblePhotoCount ?? inspection?.photoCount);
  const count = Number(fallback || 0);
  return Number.isFinite(count) ? count : 0;
}

export { flattenPhotoUrls, getInspectionPhotoCount };

function InspectionSummaryCard({ label, value, help, tone = 'slate' }) {
  const toneClass = {
    sky: 'border-sky-200 bg-sky-50 text-sky-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
    indigo: 'border-indigo-200 bg-indigo-50 text-indigo-800',
    slate: 'border-slate-200 bg-white text-slate-800',
  }[tone];

  return (
    <div className={`rounded-lg border p-4 ${toneClass}`}>
      <div className="text-xs font-semibold uppercase tracking-normal">{label}</div>
      <div className="mt-2 text-3xl font-black">{value}</div>
      {help ? <div className="mt-1 text-xs text-slate-600">{help}</div> : null}
    </div>
  );
}

function InspectionDetailModal({
  inspection,
  loading,
  actionLoading,
  onClose,
  onApprove,
  onReject,
}) {
  const { t, i18n } = useTranslation();
  const [reviewNote, setReviewNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    setReviewNote(inspection?.reviewNote || '');
    setRejectionReason(inspection?.rejectionReason || '');
  }, [inspection]);

  if (!inspection && !loading) {
    return null;
  }

  const visibleItems = getVisibleInspectionItems(inspection);
  const photoUrls = flattenPhotoUrls(inspection);
  const displayOverallStatus = getInspectionDisplayOverallStatus(inspection);
  const deviceLabel = inspection?.deviceId
    || inspection?.unit?.deviceId
    || (inspection ? t('stewardCheckinRecords.unitFallback', { id: inspection.unitId }) : t('stewardCheckinRecords.detail.loadingTitle'));

  return (
    <AdminModalSurface onClose={onClose}>
      <div className="admin-card flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg">
        <div className="flex items-start justify-between border-b border-slate-200 p-5">
          <div className="min-w-0">
            <h3 className="break-words text-2xl font-black admin-text-primary">
              {deviceLabel}
            </h3>
            {inspection ? (
              <>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {buildBadge(t, overallStatusMap[displayOverallStatus], displayOverallStatus || '-')}
                  {buildBadge(t, reviewStatusMap[inspection.reviewStatus], inspection.reviewStatus || '-')}
                </div>
                <p className="mt-2 text-sm admin-text-muted">
                  {t('stewardCheckinRecords.detail.submittedAt', { time: formatLocalizedDateTime(inspection.createdAt, i18n.language) })} | {t('stewardCheckinRecords.detail.steward', { name: inspection.stewardName || inspection.steward?.name || '-' })}
                </p>
              </>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            aria-label={t('common.close', { defaultValue: 'Close' })}
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          {loading ? (
            <div className="admin-muted-panel rounded-lg p-10 text-center admin-text-muted">
              {t('stewardCheckinRecords.detail.loading')}
            </div>
          ) : inspection ? (
            <div className="space-y-5">
              <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="admin-muted-panel rounded-lg p-4 text-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-normal admin-text-muted">{t('stewardCheckinRecords.detail.deviceInfo')}</div>
                  <div className="mt-2 font-semibold admin-text-primary">{inspection.unit?.deviceId || inspection.deviceId || '-'}</div>
                  <div className="break-words text-xs admin-text-muted">{inspection.unit?.location || t('stewardCheckinRecords.noLocation')}</div>
                </div>
                <div className="admin-muted-panel rounded-lg p-4 text-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-normal admin-text-muted">{t('stewardCheckinRecords.detail.stewardLabel')}</div>
                  <div className="mt-2 font-semibold admin-text-primary">{inspection.steward?.name || inspection.stewardName || '-'}</div>
                  <div className="text-xs admin-text-muted">{inspection.steward?.phone || '-'}</div>
                </div>
                <div className="admin-muted-panel rounded-lg p-4 text-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-normal admin-text-muted">{t('stewardCheckinRecords.detail.coordinates')}</div>
                  <div className="mt-2 break-words font-semibold admin-text-primary">{inspection.latitude || '-'}, {inspection.longitude || '-'}</div>
                </div>
                <div className="admin-muted-panel rounded-lg p-4 text-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-normal admin-text-muted">{t('stewardCheckinRecords.detail.photoCountLabel')}</div>
                  <div className="mt-2 font-semibold admin-text-primary">{t('stewardCheckinRecords.photoCount', { count: photoUrls.length })}</div>
                </div>
              </section>

              <section className="admin-muted-panel rounded-lg p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="text-sm font-semibold admin-text-primary">{t('stewardCheckinRecords.detail.summary')}</div>
                    <div className="mt-1 text-sm admin-text-secondary">{getInspectionDisplaySummary(t, inspection)}</div>
                  </div>
                  <div className="text-left text-xs admin-text-muted md:text-right">
                    <div>{t('stewardCheckinRecords.detail.start', { time: formatLocalizedDateTime(inspection.startTime, i18n.language) })}</div>
                    <div>{t('stewardCheckinRecords.detail.end', { time: formatLocalizedDateTime(inspection.endTime, i18n.language) })}</div>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="admin-card rounded-lg p-4">
                    <div className="text-xs font-semibold admin-text-muted">{t('stewardCheckinRecords.detail.reviewNote')}</div>
                    <div className="mt-2 break-words text-sm admin-text-primary">{inspection.reviewNote || t('stewardCheckinRecords.detail.noReviewNote')}</div>
                  </div>
                  <div className="admin-card rounded-lg p-4">
                    <div className="text-xs font-semibold admin-text-muted">{t('stewardCheckinRecords.detail.rejectionReason')}</div>
                    <div className="mt-2 break-words text-sm admin-text-primary">{inspection.rejectionReason || t('stewardCheckinRecords.detail.noRejection')}</div>
                  </div>
                </div>
              </section>

              <section className="admin-muted-panel rounded-lg p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold admin-text-primary">{t('stewardCheckinRecords.detail.itemDetails')}</div>
                  <div className="text-xs admin-text-muted">{t('stewardCheckinRecords.detail.itemCount', { count: visibleItems.length })}</div>
                </div>
                <div className="space-y-3">
                  {visibleItems.map((item) => (
                    <div key={item.id || item.itemId} className="admin-card rounded-lg p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                          <div className="break-words text-sm font-semibold admin-text-primary">{getInspectionItemText(t, item, 'title')}</div>
                          <div className="mt-1 text-xs admin-text-muted">{item.category}</div>
                          {item.description ? (
                            <div className="mt-2 break-words text-sm admin-text-secondary">{getInspectionItemText(t, item, 'description')}</div>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {buildBadge(t, itemStatusMap[getInspectionDisplayItemStatus(item)], getInspectionDisplayItemStatus(item))}
                          {buildBadge(t, reviewStatusMap[item.reviewStatus], item.reviewStatus || '-')}
                        </div>
                      </div>
                      <div className="mt-4 grid gap-3 md:grid-cols-4">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                          <div className="text-xs admin-text-muted">{t('stewardCheckinRecords.detail.measurement')}</div>
                          <div className="mt-1 break-words font-semibold admin-text-primary">
                            {item.measurementValue ?? '-'} {item.measurementUnit || ''}
                          </div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                          <div className="text-xs admin-text-muted">{t('stewardCheckinRecords.detail.notes')}</div>
                          <div className="mt-1 break-words font-semibold admin-text-primary">{item.notes ? getInspectionFreeText(t, item.notes) : t('common.none', { defaultValue: '-' })}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                          <div className="text-xs admin-text-muted">{t('stewardCheckinRecords.detail.watermarkStatus')}</div>
                          <div className="mt-1 break-words font-semibold admin-text-primary">
                            {t(`watermarkStatus.${item.watermarkStatus || 'unknown'}`, { defaultValue: item.watermarkStatus || '-' })}
                          </div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                          <div className="text-xs admin-text-muted">{t('stewardCheckinRecords.detail.photoCountLabel')}</div>
                          <div className="mt-1 font-semibold admin-text-primary">{t('stewardCheckinRecords.photoCount', { count: item.photoUrls?.length || 0 })}</div>
                        </div>
                      </div>
                      {item.photoUrls?.length ? (
                        <div className="mt-4 grid gap-3 md:grid-cols-3">
                          {item.photoUrls.map((url) => (
                            <InspectionPhoto
                              key={url}
                              url={url}
                              alt={getInspectionItemText(t, item, 'title')}
                              capturedAt={item.updatedAt || inspection.createdAt}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </section>

              <section className="admin-muted-panel rounded-lg p-5">
                <div className="mb-4 text-sm font-semibold admin-text-primary">{t('stewardCheckinRecords.detail.manualReview')}</div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold admin-text-muted">{t('stewardCheckinRecords.detail.reviewNote')}</label>
                    <textarea
                      value={reviewNote}
                      onChange={(event) => setReviewNote(event.target.value)}
                      rows={4}
                      placeholder={t('stewardCheckinRecords.detail.reviewNotePlaceholder')}
                      className="admin-input w-full rounded-lg px-4 py-3 text-sm outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold admin-text-muted">{t('stewardCheckinRecords.detail.rejectionReason')}</label>
                    <textarea
                      value={rejectionReason}
                      onChange={(event) => setRejectionReason(event.target.value)}
                      rows={4}
                      placeholder={t('stewardCheckinRecords.detail.rejectionPlaceholder')}
                      className="admin-input w-full rounded-lg px-4 py-3 text-sm outline-none"
                    />
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => onApprove(reviewNote)}
                    disabled={actionLoading}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold ${
                      actionLoading ? 'cursor-not-allowed bg-slate-200 text-slate-500' : 'bg-emerald-600 text-white hover:bg-emerald-700'
                    }`}
                  >
                    <ShieldCheck size={16} />
                    {t('stewardCheckinRecords.actions.approve')}
                  </button>
                  <button
                    type="button"
                    onClick={() => onReject(rejectionReason, reviewNote)}
                    disabled={actionLoading}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold ${
                      actionLoading ? 'cursor-not-allowed bg-slate-200 text-slate-500' : 'bg-rose-600 text-white hover:bg-rose-700'
                    }`}
                  >
                    <ShieldAlert size={16} />
                    {t('stewardCheckinRecords.actions.reject')}
                  </button>
                </div>
              </section>
            </div>
          ) : null}
        </div>
      </div>
    </AdminModalSurface>
  );
}

export default function StewardCheckinRecords() {
  const { t, i18n } = useTranslation();
  const [inspections, setInspections] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedInspectionId, setSelectedInspectionId] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [filters, setFilters] = useState(() => ({
    search: '',
    reviewStatus: 'all',
    ...getManilaCurrentMonthRange(),
  }));

  const listParams = useMemo(() => ({
    limit: 500,
    ...(filters.startDate ? { startDate: filters.startDate } : {}),
    ...(filters.endDate ? { endDate: filters.endDate } : {}),
  }), [filters.endDate, filters.startDate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [inspectionResult, unitsResult] = await Promise.allSettled([
        adminAPI.getInspections(listParams),
        unitAPI.getUnits(),
      ]);

      if (inspectionResult.status === 'rejected') {
        throw inspectionResult.reason;
      }
      setInspections(inspectionResult.value.data?.data?.inspections || []);

      if (unitsResult.status === 'fulfilled') {
        setUnits(unitsResult.value.data?.data || []);
      } else {
        setUnits([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('stewardCheckinRecords.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [listParams, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const assignedUnits = useMemo(
    () => units.filter((unit) => Boolean(unit.stewardId)),
    [units]
  );

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const filteredInspections = useMemo(() => {
    const keyword = filters.search.trim().toLowerCase();

    return inspections.filter((inspection) => {
      const stewardName = inspection.steward?.name || inspection.stewardName || '';
      const deviceId = inspection.deviceId || inspection.unit?.deviceId || '';
      const location = inspection.unit?.location || '';

      if (filters.reviewStatus !== 'all' && inspection.reviewStatus !== filters.reviewStatus) {
        return false;
      }

      if (!isInspectionInManilaDateRange(inspection.createdAt, filters.startDate, filters.endDate)) {
        return false;
      }

      if (!keyword) {
        return true;
      }

      return [deviceId, location, stewardName].some((value) =>
        String(value || '').toLowerCase().includes(keyword)
      );
    });
  }, [filters, inspections]);

  const summary = useMemo(() => {
    const pendingReview = filteredInspections.filter((inspection) => inspection.reviewStatus === 'pending_review').length;
    const approved = filteredInspections.filter((inspection) => inspection.reviewStatus === 'approved').length;
    const rejected = filteredInspections.filter((inspection) => inspection.reviewStatus === 'rejected').length;
    const inspectedTodayCount = units.filter((unit) => unit.inspectedToday).length;
    const assignedCount = assignedUnits.length;

    return {
      total: filteredInspections.length,
      pendingReview,
      approved,
      rejected,
      inspectedTodayCount,
      assignedCount,
      completionRate: assignedCount ? Math.round((inspectedTodayCount / assignedCount) * 100) : 0,
    };
  }, [assignedUnits.length, filteredInspections, units]);

  const openInspectionDetail = async (inspectionId) => {
    setSelectedInspectionId(inspectionId);
    setDetailLoading(true);
    setSelectedInspection(null);

    try {
      const response = await adminAPI.getInspectionDetail(inspectionId);
      setSelectedInspection(response.data?.data || null);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('stewardCheckinRecords.errors.detail'));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedInspectionId(null);
    setSelectedInspection(null);
    setDetailLoading(false);
  };

  const reloadDetailAndList = async (inspectionId) => {
    const [inspectionRes, listRes, unitsRes] = await Promise.all([
      adminAPI.getInspectionDetail(inspectionId),
      adminAPI.getInspections(listParams),
      unitAPI.getUnits(),
    ]);

    setSelectedInspection(inspectionRes.data?.data || null);
    setInspections(listRes.data?.data?.inspections || []);
    setUnits(unitsRes.data?.data || []);
  };

  const handleApprove = async (reviewNote) => {
    if (!selectedInspectionId) {
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      await adminAPI.reviewInspection(selectedInspectionId, reviewNote);
      await reloadDetailAndList(selectedInspectionId);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('stewardCheckinRecords.errors.approve'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (rejectionReason, reviewNote) => {
    if (!selectedInspectionId) {
      return;
    }

    if (!String(rejectionReason || '').trim()) {
      setError(t('stewardCheckinRecords.errors.rejectReasonRequired'));
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      await adminAPI.rejectInspection(selectedInspectionId, rejectionReason, reviewNote);
      await reloadDetailAndList(selectedInspectionId);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('stewardCheckinRecords.errors.reject'));
    } finally {
      setActionLoading(false);
    }
  };

  const renderInspectionMobileCard = (inspection) => {
    const photoCount = getInspectionPhotoCount(inspection);
    const displayOverallStatus = getInspectionDisplayOverallStatus(inspection);

    return (
      <article key={inspection.id} className="admin-card rounded-lg p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="break-words font-semibold admin-text-primary">
              {inspection.deviceId || inspection.unit?.deviceId || t('stewardCheckinRecords.unitFallback', { id: inspection.unitId })}
            </div>
            <div className="mt-1 break-words text-xs admin-text-muted">{inspection.unit?.location || t('stewardCheckinRecords.noLocation')}</div>
          </div>
          {buildBadge(t, overallStatusMap[displayOverallStatus], displayOverallStatus || '-')}
        </div>
        <div className="mt-3 grid gap-2 text-sm">
          <div>
            <span className="font-semibold admin-text-secondary">{t('stewardCheckinRecords.table.steward')}: </span>
            <span className="admin-text-primary">{inspection.steward?.name || inspection.stewardName || '-'}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {buildBadge(t, reviewStatusMap[inspection.reviewStatus], inspection.reviewStatus || '-')}
          </div>
          <div className="flex flex-wrap items-center gap-3 admin-text-secondary">
            <span className="inline-flex items-center gap-1">
              <ImageIcon size={14} />
              {t('stewardCheckinRecords.photoCount', { count: photoCount })}
            </span>
          </div>
          <div className="text-xs admin-text-muted">{formatLocalizedDateTime(inspection.createdAt, i18n.language)}</div>
        </div>
        <button
          type="button"
          onClick={() => openInspectionDetail(inspection.id)}
          className="admin-secondary-button mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-bold"
        >
          <Eye size={14} />
          {t('common.view')}
        </button>
      </article>
    );
  };

  return (
    <div className="space-y-6 p-4 admin-text-primary sm:p-6 lg:p-8">
      <header className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="min-w-0">
          <h2 className="flex items-center gap-3 text-2xl font-bold sm:text-3xl">
            <ClipboardList className="text-sky-700" size={32} />
            {t('stewardCheckinRecords.title')}
          </h2>
          <p className="mt-1 max-w-3xl text-sm admin-text-muted">
            {t('stewardCheckinRecords.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={loadData}
          className="admin-secondary-button inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          {t('common.refresh')}
        </button>
      </header>

      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      ) : null}

      <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <InspectionSummaryCard label={t('stewardCheckinRecords.cards.total')} value={summary.total} tone="sky" />
        <InspectionSummaryCard label={t('stewardCheckinRecords.cards.pending')} value={summary.pendingReview} tone="amber" />
        <InspectionSummaryCard label={t('stewardCheckinRecords.cards.approved')} value={summary.approved} tone="emerald" />
        <InspectionSummaryCard label={t('stewardCheckinRecords.cards.rejected')} value={summary.rejected} tone="rose" />
        <InspectionSummaryCard
          label={t('stewardCheckinRecords.cards.todayCompletion')}
          value={`${summary.completionRate}%`}
          help={t('stewardCheckinRecords.cards.checkedToday', { checked: summary.inspectedTodayCount, total: summary.assignedCount || 0 })}
          tone="indigo"
        />
      </section>

      <section className="admin-card space-y-4 rounded-lg p-5">
        <div className="flex items-center gap-2 text-sm font-semibold admin-text-secondary">
          <Filter size={16} className="text-sky-700" />
          {t('stewardCheckinRecords.filters.title')}
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              type="text"
              value={filters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              placeholder={t('stewardCheckinRecords.filters.searchPlaceholder')}
              className="admin-input w-full rounded-lg py-3 pl-10 pr-3 text-sm outline-none"
            />
          </div>

          <select
            value={filters.reviewStatus}
            onChange={(event) => updateFilter('reviewStatus', event.target.value)}
            className="admin-input rounded-lg px-3 py-3 text-sm outline-none"
          >
            <option value="all">{t('stewardCheckinRecords.filters.allReviewStatus')}</option>
            <option value="pending_review">{t('stewardCheckinRecords.reviewStatus.pending_review')}</option>
            <option value="approved">{t('stewardCheckinRecords.reviewStatus.approved')}</option>
            <option value="rejected">{t('stewardCheckinRecords.reviewStatus.rejected')}</option>
          </select>

          <input
            type="date"
            aria-label={t('stewardCheckinRecords.filters.startDate')}
            value={filters.startDate}
            onChange={(event) => updateFilter('startDate', event.target.value)}
            className="admin-input rounded-lg px-3 py-3 text-sm outline-none"
          />
          <input
            type="date"
            aria-label={t('stewardCheckinRecords.filters.endDate')}
            value={filters.endDate}
            onChange={(event) => updateFilter('endDate', event.target.value)}
            className="admin-input rounded-lg px-3 py-3 text-sm outline-none"
          />
        </div>
      </section>

      <section className="admin-card rounded-lg p-5">
        <div className="hidden overflow-x-auto rounded-lg border border-slate-200 lg:block">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-normal text-slate-600">
              <tr>
                <th className="p-4">{t('stewardCheckinRecords.table.device')}</th>
                <th className="p-4">{t('stewardCheckinRecords.table.steward')}</th>
                <th className="p-4">{t('stewardCheckinRecords.table.photos')}</th>
                <th className="p-4">{t('stewardCheckinRecords.table.result')}</th>
                <th className="p-4">{t('stewardCheckinRecords.table.reviewStatus')}</th>
                <th className="p-4">{t('stewardCheckinRecords.table.submittedAt')}</th>
                <th className="p-4 text-right">{t('stewardCheckinRecords.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="p-10 text-center text-slate-500">{t('stewardCheckinRecords.loading')}</td>
                </tr>
              ) : filteredInspections.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-10 text-center text-slate-500">{t('stewardCheckinRecords.empty')}</td>
                </tr>
              ) : (
                filteredInspections.map((inspection) => {
                  const photoCount = getInspectionPhotoCount(inspection);
                  const displayOverallStatus = getInspectionDisplayOverallStatus(inspection);

                  return (
                    <tr key={inspection.id} className="border-t border-slate-200 hover:bg-slate-50">
                      <td className="p-4">
                        <div className="break-words font-semibold admin-text-primary">
                          {inspection.deviceId || inspection.unit?.deviceId || t('stewardCheckinRecords.unitFallback', { id: inspection.unitId })}
                        </div>
                        <div className="mt-1 break-words text-xs admin-text-muted">{inspection.unit?.location || t('stewardCheckinRecords.noLocation')}</div>
                      </td>
                      <td className="p-4">
                        <div className="break-words font-semibold admin-text-primary">{inspection.steward?.name || inspection.stewardName || '-'}</div>
                        <div className="mt-1 text-xs admin-text-muted">{inspection.steward?.phone || '-'}</div>
                      </td>
                      <td className="p-4 admin-text-secondary">
                        <div className="inline-flex items-center gap-1 text-xs text-sky-700">
                          <ImageIcon size={14} />
                          {t('stewardCheckinRecords.photoCount', { count: photoCount })}
                        </div>
                      </td>
                      <td className="p-4">{buildBadge(t, overallStatusMap[displayOverallStatus], displayOverallStatus || '-')}</td>
                      <td className="p-4">{buildBadge(t, reviewStatusMap[inspection.reviewStatus], inspection.reviewStatus || '-')}</td>
                      <td className="p-4 admin-text-secondary">{formatLocalizedDateTime(inspection.createdAt, i18n.language)}</td>
                      <td className="p-4 text-right">
                        <button
                          type="button"
                          onClick={() => openInspectionDetail(inspection.id)}
                          className="admin-secondary-button inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold"
                        >
                          <Eye size={14} />
                          {t('common.view')}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="grid gap-3 lg:hidden">
          {loading ? (
            <div className="admin-muted-panel rounded-lg p-8 text-center admin-text-muted">{t('stewardCheckinRecords.loading')}</div>
          ) : filteredInspections.length === 0 ? (
            <div className="admin-muted-panel rounded-lg p-8 text-center admin-text-muted">{t('stewardCheckinRecords.empty')}</div>
          ) : (
            filteredInspections.map(renderInspectionMobileCard)
          )}
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <div className="admin-muted-panel rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm font-semibold admin-text-primary">
              <CheckCircle2 size={16} className="text-emerald-700" />
              {t('stewardCheckinRecords.overviews.approvedTitle')}
            </div>
            <div className="mt-2 text-sm admin-text-muted">{t('stewardCheckinRecords.overviews.approvedText', { approved: summary.approved, pending: summary.pendingReview })}</div>
          </div>
          <div className="admin-muted-panel rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm font-semibold admin-text-primary">
              <XCircle size={16} className="text-amber-700" />
              {t('stewardCheckinRecords.overviews.rejectedTitle')}
            </div>
            <div className="mt-2 text-sm admin-text-muted">{t('stewardCheckinRecords.overviews.rejectedText', { count: summary.rejected })}</div>
          </div>
        </div>
      </section>

      {selectedInspectionId ? (
        <InspectionDetailModal
          inspection={selectedInspection}
          loading={detailLoading}
          actionLoading={actionLoading}
          onClose={closeDetail}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      ) : null}
    </div>
  );
}
